/**
 * Centralized alert WebSocket handlers for SMS.
 *
 * Routes live `AlertHistory` and `AlertRoomHistory` WS frames — emitted by
 * `server/api/v2/sockets.py` via `configure_type(...)` — into React Query
 * invalidations. Shares a single entry point with the snapshot replay path
 * (`snapshotDispatcher`) so the decode → invalidate mapping lives in one
 * place and adding a new alert event type is a one-line registry change.
 *
 * WS frame shape (sockets.send_websocket_update_v2):
 *   {
 *     type: 'AlertHistory',            // or 'AlertRoomHistory'
 *     timestamp: '<ISO8601>',
 *     data: '<JSON-encoded model payload>'
 *   }
 * `parseSocketMessage` unwraps the stringified `data` so handlers receive
 * an already-parsed object as the first argument.
 */

import { activeAlertKeys, alertHistoryKeys } from '../../hooks/queries/useAlerts';
import type { HandlerDeps, SocketHandler, SocketHandlerMap } from './types';

export const ALERT_EVENT_TYPES = ['AlertHistory', 'AlertRoomHistory'] as const;

type AlertEventType = (typeof ALERT_EVENT_TYPES)[number];

function extractAlertId(data: Record<string, unknown>): number | null {
  const raw = data['id'];
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

export function createAlertHandlers(deps: HandlerDeps): SocketHandlerMap {
  const { queryClient } = deps;

  const handleAlertHistory: SocketHandler = (data) => {
    // List keys are always invalidated — the Events page and Dashboard
    // stat cards share these and must reflect activations/cancellations
    // regardless of whether this user has the detail open.
    queryClient.invalidateQueries({ queryKey: alertHistoryKeys.all });
    queryClient.invalidateQueries({ queryKey: activeAlertKeys.all });

    const id = extractAlertId(data);
    if (id != null) {
      queryClient.invalidateQueries({ queryKey: alertHistoryKeys.detail(id) });
    }
  };

  const handleAlertRoomHistory: SocketHandler = () => {
    // SMS has no dedicated AlertRoomHistory query today — the derived
    // fields (active-room counts, scoped-alert site status) that room
    // transitions affect live on the AlertHistory list, so a list
    // invalidation is sufficient to keep the UI coherent. When a
    // dedicated hook lands this handler is the single place to extend.
    queryClient.invalidateQueries({ queryKey: alertHistoryKeys.all });
    queryClient.invalidateQueries({ queryKey: activeAlertKeys.all });
  };

  const map: SocketHandlerMap = {};
  const byType: Record<AlertEventType, SocketHandler> = {
    AlertHistory: handleAlertHistory,
    AlertRoomHistory: handleAlertRoomHistory,
  };
  for (const t of ALERT_EVENT_TYPES) {
    map[t] = byType[t];
  }
  return map;
}
